import React, { useState, useMemo } from "react";
import {
  Header,
  Container,
  SpaceBetween,
  Box,
  Grid,
  Table,
  Pagination,
  TextFilter,
  Spinner,
  Select,
  SelectProps,
} from "@cloudscape-design/components";
import Plot from "react-plotly.js";
import {
  TimeGranularity,
  COLORS,
} from "./detailHelpers";

const TIME_LABEL: Record<TimeGranularity, string> = { day: "Day", week: "Week", month: "Month" };
import { PAGE_SIZE_OPTIONS } from "../constants";
import type { ProgramDetailData, RequestStatus, DetailFpyRow, DetailFpyTrend, DetailFpyTrendDataPoint } from "../../../redux/types/programTypes";

export type YieldChartTab = "all" | "smt" | "testing" | "assembly";

export type YieldTableRow = DetailFpyRow;

type FpyRow = DetailFpyRow;

const formatPercent = (value: number | null | undefined, decimals: number = 2): string => {
  if (value == null) return "—";
  return `${(value * 100).toFixed(decimals)}%`;
};

export const TestingFPYDetail: React.FC<{ programs: string[]; timeGranularity: TimeGranularity; timeValue: string | null; selectedCMs?: string[]; selectedLocations?: string[]; detailData?: ProgramDetailData | null; detailStatus?: RequestStatus }> = ({ timeGranularity, detailData = null, detailStatus = "idle" }) => {
  const [yieldPage, setYieldPage] = useState(1);
  const [yieldSearchText, setYieldSearchText] = useState("");
  const [chartTab, setChartTab] = useState<YieldChartTab>("all");
  const [expandedItems, setExpandedItems] = useState<ReadonlyArray<FpyRow>>([]);
  const [selectedPageSize, setSelectedPageSize] = useState<SelectProps.Option>(PAGE_SIZE_OPTIONS[0] as SelectProps.Option);
  const YIELD_PAGE_SIZE = parseInt(selectedPageSize.value || "10", 10);

  const tableRows = useMemo(() => {
    if (!detailData?.table) return [];
    return detailData.table as FpyRow[];
  }, [detailData]);

  // Build parent-child mapping by parent_program + cm + cm_location + stage
  const { displayItems, childrenMap } = useMemo(() => {
    const data = tableRows;
    const cMap = new Map<string, FpyRow[]>();
    const existingParentKeys = new Set<string>();

    // Identify existing parents
    data.forEach((row) => {
      if (!row.parent_program) {
        existingParentKeys.add(`${row.program}__${row.cm}__${row.cm_location}__${row.stage}`);
      }
    });

    // Gather children grouped by parent key
    data.forEach((row) => {
      if (row.parent_program) {
        const parentKey = `${row.parent_program}__${row.cm}__${row.cm_location}__${row.stage}`;
        const existing = cMap.get(parentKey) || [];
        existing.push(row);
        cMap.set(parentKey, existing);
      }
    });

    // Build display items (top-level rows)
    const items: FpyRow[] = [];
    data.forEach((row) => {
      if (!row.parent_program) {
        items.push(row);
      }
    });

    // Create virtual parent rows for orphan children
    const createdVirtualParents = new Set<string>();
    data.forEach((row) => {
      if (row.parent_program) {
        const parentKey = `${row.parent_program}__${row.cm}__${row.cm_location}__${row.stage}`;
        if (!existingParentKeys.has(parentKey) && !createdVirtualParents.has(parentKey)) {
          createdVirtualParents.add(parentKey);
          const virtualParent: FpyRow = {
            program: row.parent_program,
            parent_program: null,
            cm: row.cm,
            cm_location: row.cm_location,
            stage: row.stage,
            fpy: 0,
            cum_fpy: 0,
            rpy: null,
            cum_rpy: null,
            target: 0,
          };
          items.push(virtualParent);
        }
      }
    });

    return { displayItems: items, childrenMap: cMap };
  }, [tableRows]);

  // Filtered display data
  const filteredDisplay = useMemo(() => {
    if (!yieldSearchText.trim()) return displayItems;
    const q = yieldSearchText.toLowerCase();
    return displayItems.filter((r) => {
      const rowKey = `${r.program}__${r.cm}__${r.cm_location}__${r.stage}`;
      return (
        r.program.toLowerCase().includes(q) ||
        (`${r.cm}-${r.cm_location}`).toLowerCase().includes(q) ||
        r.stage.toLowerCase().includes(q) ||
        // Include parent if any children match
        (childrenMap.get(rowKey) || []).some((child) =>
          child.program.toLowerCase().includes(q) ||
          (`${child.cm}-${child.cm_location}`).toLowerCase().includes(q)
        )
      );
    });
  }, [displayItems, childrenMap, yieldSearchText]);

  const yieldPageItems = filteredDisplay.slice((yieldPage - 1) * YIELD_PAGE_SIZE, yieldPage * YIELD_PAGE_SIZE);

  const apiTrend = detailData?.trend as DetailFpyTrend[] | undefined;

  // Memoize chart building function and data to prevent chart re-renders on table interactions
  const buildChartData = useMemo(() => {
    return (stageFilter: string) => {
      if (!Array.isArray(apiTrend) || apiTrend.length === 0) return [];
      const barTraces: any[] = [];
      const lineTraces: any[] = [];
      const rpyLineTraces: any[] = [];
      let colorIdx = 0;
      const matchingItems = apiTrend.filter((item) =>
        item.stage && item.stage.toLowerCase().includes(stageFilter.toLowerCase())
      );
      matchingItems.forEach((item) => {
        const name = `${item.program} (${item.cm}-${item.cm_location})`;
        const xAxis = (item.data || []).map((d: DetailFpyTrendDataPoint) => d.period);
        const yInput = (item.data || []).map((d: DetailFpyTrendDataPoint) => d.actual_input != null ? d.actual_input : null);
        const yFpy = (item.data || []).map((d: DetailFpyTrendDataPoint) => d.fpy != null ? d.fpy * 100 : null);
        const yRpy = (item.data || []).map((d: DetailFpyTrendDataPoint) => d.rpy != null ? d.rpy * 100 : null);
        barTraces.push({
          x: xAxis,
          y: yInput,
          name: `${name} Input`, type: "bar" as const, marker: { color: COLORS[colorIdx % COLORS.length] }, yaxis: "y", opacity: 0.7,
          hovertemplate: `<b>${name}</b><br>Period: %{x}<br>Input Qty: %{y:,}<extra></extra>`,
        });
        lineTraces.push({
          x: xAxis,
          y: yFpy,
          name: `${name} FPY%`, type: "scatter" as const, mode: "lines+markers" as const,
          connectgaps: false,
          marker: { color: COLORS[colorIdx % COLORS.length], size: 5 }, line: { width: 2, color: COLORS[colorIdx % COLORS.length] }, yaxis: "y2",
          hovertemplate: `<b>${name}</b><br>Period: %{x}<br>FPY: %{y:.2f}%<br>Target: ${item.target != null ? (item.target * 100).toFixed(2) : "N/A"}%<extra></extra>`,
        });
        rpyLineTraces.push({
          x: xAxis,
          y: yRpy,
          name: `${name} RPY%`, type: "scatter" as const, mode: "lines" as const,
          connectgaps: false,
          marker: { color: COLORS[colorIdx % COLORS.length], size: 5 }, line: { width: 2, color: COLORS[colorIdx % COLORS.length], dash: "dash" }, yaxis: "y2",
          hovertemplate: `<b>${name}</b><br>Period: %{x}<br>RPY: %{y:.2f}%<br>Target: ${item.target != null ? (item.target * 100).toFixed(2) : "N/A"}%<extra></extra>`,
        });
        colorIdx++;
      });
      return [...barTraces, ...lineTraces, ...rpyLineTraces];
    };
  }, [apiTrend]);

  const chartConfigs = useMemo(() => {
    return chartTab === "all"
      ? [
          { title: "SMT Yield Trend", data: buildChartData("SMT") },
          { title: "Testing Yield Trend", data: buildChartData("Testing") },
          { title: "Assembly Yield Trend", data: buildChartData("Assembly") },
        ]
      : [
          { title: `${chartTab === "smt" ? "SMT" : chartTab === "testing" ? "Testing" : "Assembly"} Yield Trend`,
            data: buildChartData(chartTab === "smt" ? "SMT" : chartTab === "testing" ? "Testing" : "Assembly") },
        ];
  }, [chartTab, buildChartData]);

  const legendPrograms = useMemo(() => {
    const programs: string[] = [];
    if (Array.isArray(apiTrend)) {
      apiTrend.forEach((item) => {
        const name = `${item.program} (${item.cm}-${item.cm_location})`;
        if (programs.indexOf(name) === -1) {
          programs.push(name);
        }
      });
    }
    return programs;
  }, [apiTrend]);

  const allTabChartLayout = useMemo(() => ({
    height: 320, margin: { t: 30, b: 80, l: 45, r: 45 },
    barmode: "group" as const,
    showlegend: false,
    yaxis: { title: "Input Qty", side: "left" as const, rangemode: "nonnegative" as const, exponentformat: "SI" as const, showline: true, linewidth: 1, linecolor: "#232f3e", automargin: true },
    yaxis2: { title: "Yield %", overlaying: "y" as const, side: "right" as const, ticksuffix: "%", showline: true, linewidth: 1, linecolor: "#232f3e", automargin: true },
    xaxis: { title: timeGranularity === "week" ? "Week" : "Month", tickangle: -45, type: "category" as const, showline: true, linewidth: 1, linecolor: "#232f3e" },
    font: { size: 9 },
  }), [timeGranularity]);

  const singleTabChartLayout = useMemo(() => ({
    height: 560, margin: { t: 30, b: 80, l: 60, r: 60 },
    barmode: "group" as const,
    yaxis: { title: "Input Qty", side: "left" as const, rangemode: "nonnegative" as const, exponentformat: "SI" as const, showline: true, linewidth: 1, linecolor: "#232f3e", automargin: true, domain: [0.3, 1] },
    yaxis2: { title: "Yield %", overlaying: "y" as const, side: "right" as const, ticksuffix: "%", showline: true, linewidth: 1, linecolor: "#232f3e", automargin: true },
    xaxis: { title: timeGranularity === "week" ? "Week" : "Month", tickangle: -45, type: "category" as const, showline: true, linewidth: 1, linecolor: "#232f3e", automargin: true },
    legend: { orientation: "h" as const, y: 0, yanchor: "top" as const, xanchor: "center" as const, x: 0.5, font: { size: 10 } },
    font: { size: 11 },
  }), [timeGranularity]);

  const chartConfig = useMemo(() => ({ displayModeBar: false, responsive: true }), []);

  if (detailStatus === "loading") {
    return (
      <Container header={<Header variant="h2">{`Production Yield — by ${TIME_LABEL[timeGranularity]}`}</Header>}>
        <Box textAlign="center" padding={{ vertical: "xl" }}>
          <SpaceBetween size="s" alignItems="center">
            <Spinner size="large" />
            <Box color="text-body-secondary">Loading detail data...</Box>
          </SpaceBetween>
        </Box>
      </Container>
    );
  }

  return (
    <div data-testid="program-detail-yield">
    <Container header={<Header variant="h2">{`Production Yield — by ${TIME_LABEL[timeGranularity]}`}</Header>}>
      <SpaceBetween size="l">
        {/* Yield Table */}
        <div data-testid="program-table-yield">
        <Table<FpyRow>
          trackBy={(item: FpyRow) => `${item.program}__${item.cm}__${item.cm_location}__${item.stage}`}
          columnDefinitions={[
            { id: "program", header: "Program", width: 150, cell: (item: FpyRow) => {
              const rowKey = `${item.program}__${item.cm}__${item.cm_location}__${item.stage}`;
              const hasChildren = (childrenMap.get(rowKey) || []).length > 0;
              if (hasChildren || item.parent_program) {
                const childCount = (childrenMap.get(rowKey) || []).length;
                if (!item.parent_program && childCount > 0) {
                  return (
                    <span className="detail-expandable-program">
                      <span className="detail-program-cell">{item.program}</span>
                      <span className="detail-child-count-badge">{childCount}</span>
                    </span>
                  );
                }
                return <span className="detail-program-cell">{item.program}</span>;
              }
              const idx = yieldPageItems.indexOf(item);
              const prevItem = yieldPageItems[idx - 1];
              const groupChanged = idx === 0 || prevItem?.program !== item.program || prevItem?.cm !== item.cm || prevItem?.cm_location !== item.cm_location;
              return groupChanged ? <span className="detail-program-cell">{item.program}</span> : "";
            }},
            { id: "cmLocation", header: "CM & Location", width: 90, cell: (item: FpyRow) => {
              if (item.parent_program) return "";
              const rowKey = `${item.program}__${item.cm}__${item.cm_location}__${item.stage}`;
              const hasChildren = (childrenMap.get(rowKey) || []).length > 0;
              if (hasChildren) {
                return <span className="detail-cm-cell">{`${item.cm}-${item.cm_location}`}</span>;
              }
              const idx = yieldPageItems.indexOf(item);
              const prevItem = yieldPageItems[idx - 1];
              const groupChanged = idx === 0 || prevItem?.program !== item.program || prevItem?.cm !== item.cm || prevItem?.cm_location !== item.cm_location;
              return groupChanged ? <span className="detail-cm-cell">{`${item.cm}-${item.cm_location}`}</span> : "";
            }},
            { id: "stage", header: "Stage", cell: (item: FpyRow) => item.stage, width: 70 },
            { id: "target", header: "Target", width: 65, cell: (item: FpyRow) => formatPercent(item.target, 1) },
            { id: "fpyActual", header: "FPY Actual", width: 80, cell: (item: FpyRow) => formatPercent(item.fpy) },
            { id: "fpyCum", header: "FPY Cum", width: 75, cell: (item: FpyRow) => formatPercent(item.cum_fpy) },
            { id: "rpyActual", header: "RPY Actual", width: 80, cell: (item: FpyRow) => formatPercent(item.rpy) },
            { id: "rpyCum", header: "RPY Cum", width: 75, cell: (item: FpyRow) => formatPercent(item.cum_rpy) },
          ]}
          items={yieldPageItems}
          expandableRows={{
            getItemChildren: (item: FpyRow) => {
              const rowKey = `${item.program}__${item.cm}__${item.cm_location}__${item.stage}`;
              return childrenMap.get(rowKey) || [];
            },
            isItemExpandable: (item: FpyRow) => {
              const rowKey = `${item.program}__${item.cm}__${item.cm_location}__${item.stage}`;
              return (childrenMap.get(rowKey) || []).length > 0;
            },
            expandedItems: expandedItems,
            onExpandableItemToggle: ({ detail }) => {
              const item = detail.item;
              const isExpanded = detail.expanded;
              const getKey = (i: FpyRow) => `${i.program}__${i.cm}__${i.cm_location}__${i.stage}`;
              if (isExpanded) {
                setExpandedItems((prev) => [...prev, item]);
              } else {
                setExpandedItems((prev) => prev.filter((i) => getKey(i) !== getKey(item)));
              }
            },
          }}
          variant="embedded"
          filter={<TextFilter filteringText={yieldSearchText} onChange={({ detail }) => { setYieldSearchText(detail.filteringText); setYieldPage(1); }} filteringPlaceholder="Search by program, CM or CM location..." />}
          empty={<Box textAlign="center" color="inherit" padding={{ vertical: "l" }}><b>No yield data</b><Box color="text-body-secondary" padding={{ top: "s" }}>No production yield records available for the selected period.</Box></Box>}
          pagination={
            <div className="detail-table-toolbar">
              <div className="detail-page-size-selector">
                <span className="detail-page-size-label">Page size:</span>
                <div className="detail-page-size-select">
                  <Select
                    selectedOption={selectedPageSize}
                    onChange={({ detail }) => { setSelectedPageSize(detail.selectedOption as SelectProps.Option); setYieldPage(1); }}
                    options={PAGE_SIZE_OPTIONS}
                  />
                </div>
              </div>
              {filteredDisplay.length > YIELD_PAGE_SIZE && (
                <div data-testid="program-table-yield-pagination">
                  <Pagination currentPageIndex={yieldPage} pagesCount={Math.ceil(filteredDisplay.length / YIELD_PAGE_SIZE)} onChange={({ detail }) => setYieldPage(detail.currentPageIndex)} />
                </div>
              )}
            </div>
          }
        />
        </div>

        {/* Separator */}
        <div className="detail-separator" />

        {/* Chart Tab Selector */}
        <div className="fpy-chart-tab-container">
          <div className="fpy-chart-tab-group" data-testid="program-yield-chart-tabs" data-active-tab={chartTab}>
            {([["all", "All"], ["smt", "SMT"], ["testing", "Testing"], ["assembly", "Assembly"]] as [YieldChartTab, string][]).map(([id, label]) => (
              <span
                key={id}
                data-testid={`program-yield-chart-tab-${id}`}
                data-active={String(chartTab === id)}
                onClick={() => setChartTab(id)}
                className={`fpy-chart-tab ${chartTab === id ? "fpy-chart-tab-active" : "fpy-chart-tab-inactive"}`}
              >
                {label}
              </span>
            ))}
          </div>
        </div>

        {/* Yield Trend Charts */}
        {chartTab === "all" ? (
        <div data-testid="program-yield-charts-all">
          <Grid gridDefinition={chartConfigs.map(() => ({ colspan: Math.floor(12 / chartConfigs.length) as 4 | 6 | 12 }))}>
            {chartConfigs.map((cfg) => {
              const titleKebab = cfg.title.toLowerCase().replace(/\s+/g, "-");
              return (
              <div key={cfg.title} data-testid={`program-yield-chart-${titleKebab}`}>
                <Box variant="h4" textAlign="center" padding={{ bottom: "xs" }}><span data-testid={`program-yield-chart-${titleKebab}-title`}>{cfg.title}</span></Box>
                <div style={{ overflow: "visible", width: "100%" }}>
                <Plot
                  data={cfg.data}
                  layout={allTabChartLayout}
                  config={chartConfig}
                  style={{ width: "100%", overflow: "visible" }}
                />
                </div>
              </div>
              );
            })}
          </Grid>
          {legendPrograms.length > 0 && (
          <div className="fpy-legend-container" data-testid="program-yield-legend">
            <div className="fpy-legend-items-row">
              {legendPrograms.map((name, idx) => (
                <div key={name} className="fpy-legend-item">
                  <span className="fpy-legend-color" style={{ backgroundColor: COLORS[idx % COLORS.length] }} />
                  <span>{name}</span>
                </div>
              ))}
            </div>
            <div className="fpy-legend-meta">
              <span className="fpy-legend-line" /> FPY%
              <span className="fpy-legend-line-dashed" /> RPY%
              <span className="fpy-legend-bar" /> Input
            </div>
          </div>
          )}
        </div>
        ) : (
        <div data-testid={`program-yield-chart-${chartTab}`}>
          <Box variant="h4" textAlign="center" padding={{ bottom: "xs" }}><span data-testid={`program-yield-chart-${chartTab}-title`}>{chartConfigs[0].title}</span></Box>
          <div style={{ overflow: "visible", width: "100%" }}>
          <Plot
            data={chartConfigs[0].data}
            layout={singleTabChartLayout}
            config={chartConfig}
            style={{ width: "100%", overflow: "visible" }}
          />
          </div>
        </div>
        )}
      </SpaceBetween>
    </Container>
    </div>
  );
};
